import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

interface SearchContextValue {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  searchFocused: boolean;
  setSearchFocused: (f: boolean) => void;
  resetSearch: () => void;
  /** True while the catalogue floating search bar is on screen. */
  floatingSearchActive: boolean;
  setFloatingSearchActive: (v: boolean) => void;
}

const SearchContext = createContext<SearchContextValue | null>(null);

/**
 * Shared mobile/desktop search state so that the header search bar and the
 * floating catalogue toolbar operate on one single query + focus state.
 */
export const SearchProvider = ({ children }: { children: ReactNode }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [floatingSearchActive, setFloatingSearchActive] = useState(false);

  const resetSearch = useCallback(() => {
    setSearchQuery("");
    setSearchFocused(false);
  }, []);

  const value = useMemo(
    () => ({
      searchQuery,
      setSearchQuery,
      searchFocused,
      setSearchFocused,
      resetSearch,
      floatingSearchActive,
      setFloatingSearchActive,
    }),
    [searchQuery, searchFocused, resetSearch, floatingSearchActive],
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

export const useSearchState = (): SearchContextValue => {
  const ctx = useContext(SearchContext);
  if (!ctx) {
    throw new Error("useSearchState must be used within a SearchProvider");
  }
  return ctx;
};
